/**
 * Canonical factory match tile titles and display order (legacy Dato layout).
 *
 * Grid order (left-to-right, top-to-bottom in 2 columns):
 * 1. Seat diagram schematic
 * 2. Kit
 * 3. Pattern
 * 4. Tone
 * 5. Perforation
 * 6. Primary color
 * 7. Insert color
 * 8. Stitch
 */

/** PDM `digital_asset_type_code` → UI card title */
export const FACTORY_MATCH_ASSET_TYPE_TITLES: Record<string, string> = {
  SD: "Seat diagram schematic",
  FED: "Seat diagram schematic",
  FD: "Seat diagram schematic",
  KT: "Kit",
  FP: "Pattern",
  PT: "Pattern",
  TN: "Tone",
  PF: "Perforation",
  PL: "Perforation",
  PC: "Primary color",
  IC: "Insert color",
  ST: "Stitch",
}

/** Alternate titles normalized before sort */
const TITLE_SORT_ALIASES: Record<string, string> = {
  fed: "seat diagram schematic",
  "f.p": "pattern",
  "f.p.": "pattern",
  "perforation location": "perforation",
  "primary colour": "primary color",
  "insert colour": "insert color",
  "seat diagram": "seat diagram schematic",
}

export const FACTORY_MATCH_OPTION_DISPLAY_ORDER = [
  "seat diagram schematic",
  "kit",
  "pattern",
  "tone",
  "perforation",
  "primary color",
  "insert color",
  "stitch",
] as const

function normalizeTitleForSort(title: string): string {
  const key = title.trim().toLowerCase()
  return TITLE_SORT_ALIASES[key] ?? key
}

/** Normalized key for deduping factory match tiles (e.g. PL vs text fallback). */
export function normalizeFactoryMatchTitleKey(title: string): string {
  return normalizeTitleForSort(title)
}

const CANONICAL_FACTORY_MATCH_TITLES: Record<string, string> = {
  "seat diagram schematic": "Seat diagram schematic",
  kit: "Kit",
  pattern: "Pattern",
  tone: "Tone",
  perforation: "Perforation",
  "primary color": "Primary color",
  "insert color": "Insert color",
  stitch: "Stitch",
}

/** UI label for factory match option tiles (legacy aliases → canonical title). */
export function canonicalizeFactoryMatchOptionTitle(title: string): string {
  const key = normalizeTitleForSort(title)
  return CANONICAL_FACTORY_MATCH_TITLES[key] ?? title.trim()
}

function displayOrderIndex(title: string): number {
  const key = normalizeTitleForSort(title)
  const index = (
    FACTORY_MATCH_OPTION_DISPLAY_ORDER as readonly string[]
  ).indexOf(key)
  return index === -1 ? FACTORY_MATCH_OPTION_DISPLAY_ORDER.length : index
}

/**
 * Resolve UI title from PDM asset type (and filename hint for seat diagrams).
 */
export function resolveFactoryMatchOptionTitle(
  assetType: string,
  imageFilename = ""
): string {
  const code = assetType.trim().toUpperCase()
  if (code && FACTORY_MATCH_ASSET_TYPE_TITLES[code]) {
    return FACTORY_MATCH_ASSET_TYPE_TITLES[code]
  }

  const lowerFile = imageFilename.toLowerCase()
  if (lowerFile.includes("-sd") || lowerFile.includes("_sd")) {
    return "Seat diagram schematic"
  }

  if (code === "FED" || code === "FD") {
    return "Seat diagram schematic"
  }

  return code ? code : "Option"
}

export function sortFactoryMatchOptionsByDisplayOrder<
  T extends { title: string }
>(options: T[]): T[] {
  return [...options].sort(
    (a, b) => displayOrderIndex(a.title) - displayOrderIndex(b.title)
  )
}

export function sortFactoryMatchOptionGroupKeys(titles: string[]): string[] {
  return [...titles].sort((a, b) => displayOrderIndex(a) - displayOrderIndex(b))
}
